import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../services/api';


function ServiceTemplatesManager({ companyId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    estimated_duration_minutes: 60,
    default_price: '',
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, [companyId]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await bookingAPI.getServiceTemplates(false, companyId);
      setTemplates(response.data.templates || []);
      setError('');
    } catch (err) {
      setError('Failed to load service templates');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const data = {
        ...formData,
        estimated_duration_minutes: parseInt(formData.estimated_duration_minutes),
        default_price: formData.default_price ? parseFloat(formData.default_price) : null,
        company_id: companyId
      };

      await bookingAPI.createServiceTemplate(data);
      setSuccess('Service template created successfully!');
      setShowAddModal(false);
      resetForm();
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create service template');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const data = {
        ...formData,
        estimated_duration_minutes: parseInt(formData.estimated_duration_minutes),
        default_price: formData.default_price ? parseFloat(formData.default_price) : null
      };

      await bookingAPI.updateServiceTemplate(currentTemplate.id, data);
      setSuccess('Service template updated successfully!');
      setShowEditModal(false);
      resetForm();
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update service template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this service template? This cannot be undone if it has been used in bookings.')) {
      return;
    }

    try {
      await bookingAPI.deleteServiceTemplate(templateId);
      setSuccess('Service template deleted successfully!');
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete service template');
    }
  };

  const openEditModal = (template) => {
    setCurrentTemplate(template);
    setFormData({
      name: template.name || '',
      description: template.description || '',
      estimated_duration_minutes: template.estimated_duration_minutes || 60,
      default_price: template.default_price || '',
      is_active: template.is_active !== undefined ? template.is_active : true
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      estimated_duration_minutes: 60,
      default_price: '',
      is_active: true
    });
    setCurrentTemplate(null);
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return <div className="loading">Loading service templates...</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Service Templates</h2>
        <button onClick={() => {
          resetForm();
          setShowAddModal(true);
        }} className="btn btn-primary">
          Add New Template
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {templates.length === 0 ? (
          <p style={{padding: '20px', textAlign: 'center', color: 'var(--color-gray)'}}>
            No service templates found. Create your first template to get started.
          </p>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px',
            padding: '16px'
          }}>
            {templates.map((template) => (
              <div key={template.id} style={{
                padding: '16px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                {/* Top row: Name + Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: '15px', color: '#fff' }}>{template.name}</strong>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      color: '#ffffff',
                      backgroundColor: template.is_active ? '#4A8C18' : '#6b7280',
                      flexShrink: 0
                    }}
                  >
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Description */}
                {template.description && (
                  <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>
                    {template.description}
                  </p>
                )}

                {/* Info chips + Actions */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      background: 'rgba(59, 130, 246, 0.12)',
                      color: '#60a5fa',
                      fontWeight: '600'
                    }}>
                      {formatDuration(template.estimated_duration_minutes)}
                    </span>
                    {template.default_price && (
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        background: 'rgba(34, 197, 94, 0.12)',
                        color: '#22c55e',
                        fontWeight: '600'
                      }}>
                        £{parseFloat(template.default_price).toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => openEditModal(template)}
                      className="btn btn-secondary btn-sm"
                      style={{ padding: '6px 14px', fontSize: '12px', minHeight: '36px' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="btn btn-danger btn-sm"
                      style={{ padding: '6px 14px', fontSize: '12px', minHeight: '36px' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Template Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          resetForm();
        }}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Service Template</h2>
              <button className="close-btn" onClick={() => {
                setShowAddModal(false);
                resetForm();
              }}>×</button>
            </div>
            
            <form onSubmit={handleAdd}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px'}}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="e.g., Basic Service, Full Service"
                  />
                </div>

                <div className="form-group">
                  <label>Estimated Duration (minutes) *</label>
                  <input
                    type="number"
                    value={formData.estimated_duration_minutes}
                    onChange={(e) => setFormData({...formData, estimated_duration_minutes: parseInt(e.target.value) || 0})}
                    required
                    min={15}
                    step={15}
                    placeholder="60"
                  />
                  <p style={{fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '5px'}}>
                    Duration: {formatDuration(formData.estimated_duration_minutes)}
                  </p>
                </div>

                <div className="form-group">
                  <label>Default Price (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.default_price}
                    onChange={(e) => setFormData({...formData, default_price: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  placeholder="Describe what this service includes..."
                />
              </div>

              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    style={{width: '18px', height: '18px'}}
                  />
                  Active (available for booking)
                </label>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">Create Template</button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {showEditModal && currentTemplate && (
        <div className="modal-overlay" onClick={() => {
          setShowEditModal(false);
          resetForm();
        }}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Edit Service Template</h2>
              <button className="close-btn" onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}>×</button>
            </div>
            
            <form onSubmit={handleEdit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Estimated Duration (minutes) *</label>
                <input
                  type="number"
                  value={formData.estimated_duration_minutes}
                  onChange={(e) => setFormData({...formData, estimated_duration_minutes: parseInt(e.target.value) || 0})}
                  required
                  min={15}
                  step={15}
                />
                <p style={{fontSize: '12px', color: 'var(--color-gray)', marginTop: '5px'}}>
                  Duration: {formatDuration(formData.estimated_duration_minutes)}
                </p>
              </div>

              <div className="form-group">
                <label>Default Price (optional)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.default_price}
                  onChange={(e) => setFormData({...formData, default_price: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', wordBreak: 'break-word'}}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    style={{width: '18px', height: '18px', flexShrink: 0}}
                  />
                  <span style={{flex: 1}}>Active (available for booking)</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">Update Template</button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceTemplatesManager;









